import React from 'react';
import Chart from 'react-apexcharts';
import { Card, CardContent, Typography, Box, Avatar, Skeleton } from '@mui/material';

// --- Helper Components & Functions (re-used from your code) ---

const FallbackAvatar = () => (
	<svg width="28" height="28" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
		<circle cx="16" cy="16" r="16" fill="#E0E0E0"/>
		<path d="M16 18C19.3137 18 22 15.3137 22 12C22 8.68629 19.3137 6 16 6C12.6863 6 10 8.68629 10 12C10 15.3137 12.6863 18 16 18ZM16 21C11.5817 21 8 23.2386 8 26H24C24 23.2386 20.4183 21 16 21Z" fill="#BDBDBD"/>
	</svg>
);

const getScoreColor = (score) => {
	if (score > 60) return '#4CAF50'; // Green
	if (score > 30) return '#FFC107'; // Orange
	return '#F44336'; // Red
};


const PerformanceScoreWidget = ({ details, isLoading }) => {

	// --- Data Processing ---
	const topPerformer = !isLoading && details && details.length > 0 ? details[0] : {};
	// Changed to show all individuals to make the list scrollable
	const allIndividuals = !isLoading && details ? details : [];
	const overallScore = Math.round(topPerformer.percentage || 0);
	const sparklineSeriesData = !isLoading && details
		? [...details].reverse().map(manager => manager.total_sales)
		: [];
// This is the perfected version based on your correct starting point
	const sparklineOptions = {
		chart: { type: 'area', height: 80, sparkline: { enabled: true } },
		stroke: { curve: 'smooth', width: 2, colors: [getScoreColor(overallScore)] },
		fill: {
			type: 'gradient',
			gradient: {
				shadeIntensity: 1, opacityFrom: 0.6, opacityTo: 0, stops: [0, 100],
				colorStops: [{ offset: 0, color: getScoreColor(overallScore), opacity: 0.4 }]
			},
		},
		tooltip: { enabled: false }, markers: { size: 0 }, grid: { show: false },
	};

	const sparklineSeries = [{ name: 'Sales Trend', data: sparklineSeriesData }];

	return (
		<Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgb(0 0 0 / 0.05)', height: '280px' }}>
			<CardContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
				{/* Header */}
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<div className={`text-lg fw-500`}>
						Performance Score
					</div>
				</Box>

				{isLoading ? (
					<Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
						<Skeleton variant="text" width="40%" height={50} sx={{ alignSelf: 'center' }} />
						<Skeleton variant="rectangular" height={80} sx={{ my: 1, borderRadius: 2 }} />
						<Skeleton variant="text" height={25} />
						<Skeleton variant="text" height={25} />
					</Box>
				) : (
					<>
						{/* Overall Score and Chart */}
						<Box sx={{ textAlign: 'center', mt: 1 }}>
							<Typography variant="h3" fontWeight="bold" sx={{ color: getScoreColor(overallScore), fontSize: '36px' }}>
								{overallScore}%
							</Typography>
							<Box sx={{ height: '80px', width: '100%', overflow: 'hidden', mt: -1 }}>
								{sparklineSeriesData.length > 0 && (
									<Chart options={sparklineOptions} series={sparklineSeries} type="area" height={80} />
								)}
							</Box>
						</Box>

						{/* Individuals List - NOW SCROLLABLE */}
						<Box sx={{
							flexGrow: 1,
							mt: 1,
							overflowY: 'auto', // This makes the list scrollable
							pr: 1, // Padding to prevent scrollbar from overlapping content
							// Custom scrollbar styling
							'&::-webkit-scrollbar': { width: '5px' },
							'&::-webkit-scrollbar-track': { background: '#f1f1f1' },
							'&::-webkit-scrollbar-thumb': { background: '#ccc', borderRadius: '5px' },
							'&::-webkit-scrollbar-thumb:hover': { background: '#aaa' },
						}}>
							{allIndividuals.map((person) => (
								<Box key={person.id} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
									{person.profile_photo_url ? (
										<Avatar src={person.profile_photo_url} sx={{ width: 28, height: 28 }} />
									) : (
										<FallbackAvatar />
									)}
									<Typography variant="body2" sx={{ flexGrow: 1, ml: 1.5, fontSize: '13px' }}>{person.name}</Typography>
									<Typography variant="body2" fontWeight="medium" sx={{ mr: 1.5, fontSize: '13px' }}>{Math.round(person.percentage)}%</Typography>
									<Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: getScoreColor(person.percentage) }} />
								</Box>
							))}
						</Box>
					</>
				)}
			</CardContent>
		</Card>
	);
};

export default PerformanceScoreWidget;